import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Alert,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getPortfolioSummary } from '../../api/portfolio';
import { formatCurrency } from '../../utils/Currency';
import { getErrorMessage } from '../../utils/errors';
import { useCurrency } from '../../contexts/CurrencyContext';

const ROWS = [
  { key: 'aporte_liquido', label: 'Aporte Líquido' },
  { key: 'custo_posicoes_atuais', label: 'Custo das Posições Atuais' },
  { key: 'caixa', label: 'Caixa' },
  { key: 'resultado_realizado', label: 'Resultado Realizado', highlightNegative: true },
  { key: 'resultado_nao_realizado', label: 'Resultado Não-Realizado' },
];

const parseDecimal = (value) => {
  if (value === undefined || value === null) return 0;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const computeTotalResult = (realized, unrealized) =>
  parseDecimal(realized) + parseDecimal(unrealized);

const computeProfitability = (totalResult, netDeposits) => {
  const deposits = parseDecimal(netDeposits);
  if (deposits === 0) return 0;
  return (totalResult / deposits) * 100;
};

const SummaryRow = ({ label, value, currency, color, testId }) => (
  <Box
    data-testid={`row-${testId}`}
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      data-testid={`value-${testId}`}
      variant="body2"
      sx={{ fontWeight: 600, color: color || 'text.primary' }}
    >
      {formatCurrency(currency, value)}
    </Typography>
  </Box>
);

const SkeletonRows = () => (
  <Box>
    {Array.from({ length: 7 }).map((_, i) => (
      <Box
        key={i}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          py: 0.75,
        }}
      >
        <Skeleton width="40%" height={24} />
        <Skeleton width="25%" height={24} />
      </Box>
    ))}
  </Box>
);

const PortfolioSummary = () => {
  const theme = useTheme();
  const { currency } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPortfolioSummary(currency);
      setData(response.data);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [currency]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const isBackfillPending = data?.backfill_pending === true;
  const isDegraded = data?.market_data_status === 'DEGRADED';

  const totalResult = data
    ? computeTotalResult(data.resultado_realizado, data.resultado_nao_realizado)
    : 0;

  const profitability = data
    ? computeProfitability(totalResult, data.aporte_liquido)
    : 0;

  const getValueColor = (key, value) => {
    const row = ROWS.find((r) => r.key === key);
    if (row?.highlightNegative && parseDecimal(value) < 0) {
      return theme.palette.error.main;
    }
    return undefined;
  };

  const renderContent = () => {
    if (loading) {
      return <SkeletonRows />;
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      );
    }

    if (isBackfillPending) {
      return (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Processando histórico de operações...
          </Alert>
          <SkeletonRows />
        </Box>
      );
    }

    return (
      <Box>
        {isDegraded && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Dados de mercado parcialmente indisponíveis. Valores podem estar desatualizados.
          </Alert>
        )}

        {ROWS.map((row) => (
          <SummaryRow
            key={row.key}
            label={row.label}
            value={parseDecimal(data[row.key])}
            currency={currency}
            color={getValueColor(row.key, data[row.key])}
            testId={row.key}
          />
        ))}

        <Divider sx={{ my: 1 }} />

        <SummaryRow
          label="Resultado Total"
          value={totalResult}
          currency={currency}
          color={totalResult < 0 ? theme.palette.error.main : theme.palette.success.main}
          testId="total_result"
        />

        <Box
          data-testid="row-profitability"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 0.75,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Rentabilidade (%)
          </Typography>
          <Typography
            data-testid="value-profitability"
            variant="body2"
            sx={{
              fontWeight: 600,
              color: profitability < 0
                ? theme.palette.error.main
                : theme.palette.success.main,
            }}
          >
            {profitability.toFixed(2)}%
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <SummaryRow
          label="Valor de Mercado"
          value={parseDecimal(data.valor_mercado)}
          currency={currency}
          color={theme.palette.primary.main}
          testId="market_value"
        />
      </Box>
    );
  };

  return (
    <Card sx={{ borderRadius: 3, mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Resumo do Portfólio
        </Typography>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default PortfolioSummary;
